import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loading from '../../components/common/Loading';
import apiClient from '../../api/client';

/**
 * SSO Login Component
 * Handles Single Sign-On from 360Restoran
 * Accepts 360Restoran JWT token and logs in user
 */
const SsoLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('Giriş yapılıyor...');
  const hasAttemptedLogin = useRef(false);

  useEffect(() => {
    // Prevent duplicate requests in StrictMode
    if (hasAttemptedLogin.current) {
      return;
    }
    hasAttemptedLogin.current = true;

    const token = searchParams.get('token');
    const returnUrl = searchParams.get('returnUrl');

    if (!token) {
      setError('360Restoran token bulunamadı');
      // 360Restoran'a yönlendir
      setTimeout(() => {
        window.location.href = 'https://360restoran.com/auth/cover-login';
      }, 2000);
      return;
    }

    const performSsoLogin = async () => {
      try {
        setStatus('360Restoran\'dan giriş yapılıyor...');
        
        // SSO endpoint'ine istek at
        const response = await apiClient.post('/auth/sso-login', {
          token,
          returnUrl: returnUrl || 'https://360restoran.com/#/welcome',
        });

        const { accessToken, refreshToken: newRefreshToken, user, tenant } = response.data;

        // Token'ları kaydet
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        // Return URL'i kaydet
        if (returnUrl) {
          localStorage.setItem('returnUrl', returnUrl);
          window.dispatchEvent(new Event('returnUrlUpdated'));
        }
        
        setStatus('Başarılı! Yönlendiriliyorsunuz...');
        
        console.log('SSO Login successful:', { user, tenant });
        
        // AuthContext'in token'ları algılaması için custom event tetikle
        window.dispatchEvent(new Event('authChanged'));
        
        // Redirect to dashboard - biraz daha uzun bekle ki AuthContext yüklensin
        setTimeout(() => navigate('/dashboard'), 1000);
      } catch (err: any) {
        console.error('SSO Login failed:', err);
        setError(err.response?.data?.message || err.message || 'SSO girişi başarısız');
        // 360Restoran'a yönlendir
        setTimeout(() => {
          window.location.href = 'https://360restoran.com/auth/cover-login';
        }, 3000);
      }
    };

    performSsoLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Giriş Başarısız
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              360Restoran'a yönlendiriliyorsunuz...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        <div className="text-center">
          <div className="mb-6">
            <Loading />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            360Restoran'dan Giriş
          </h2>
          <p className="text-gray-600">{status}</p>
          <div className="mt-6 flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SsoLogin;
